import React, { useState, useRef, useEffect } from 'react';
import {
  Row, Col, Card, Button, Collapse
} from 'react-bootstrap';
import { Tabulator, PageModule, DownloadModule, ExportModule, SortModule, FormatModule } from "tabulator-tables";
import "tabulator-tables/dist/css/tabulator.min.css";
import * as d3 from "d3";

Tabulator.registerModule([PageModule, DownloadModule, ExportModule, SortModule, FormatModule])

interface EntityPopulation {
  census_population_long_id: string;
  entity_id: string;
  entity_name: string;
  census_year: number;
  census_population: number;
  census_summary_level: string;
  census_state_fips_code: string;
  census_county_fips_code: string;
  census_place_fips_code: string;
  census_area_name: string;
}

interface EntityPopulationProps {
    entityPopulationData?: EntityPopulation[];
    parentEntityName?: string;
}

const EntityPopulationOverview: React.FC<EntityPopulationProps> = ({
    entityPopulationData,
    parentEntityName,
}) => {
    const [showHeaderInfo, setShowHeaderInfo] = useState<boolean>(false);
    const [uniqueYearEntityPopulationData, setUniqueYearEntityPopulationData] = useState<EntityPopulation[]>([]);
    const [undefinedData, setUndefinedData] = useState<boolean>(false);
    const chartRef = useRef<SVGSVGElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const tabulatorRef = useRef<InstanceType<typeof Tabulator> | null>(null);
    const tableRef = useRef<HTMLDivElement>(null);

    const handleDownload = (type: "csv" | "xlsx" | "html" | "json") => {  
      const formattedEntityName = parentEntityName?.toLowerCase()?.replaceAll(" ", "_") || "Entity";
      if (!tabulatorRef.current) return;
      tabulatorRef.current.download(type, `${formattedEntityName}_population.${type}`);
    };

    // Added to sort out unique values
    useEffect(() => {
        const uniqueYearData = entityPopulationData?.filter((value, index, self) =>
          index === self.findIndex((i) => i.census_year === value.census_year)
        ) ?? [];
        const hasUndefinedData = entityPopulationData?.every(
          item => item.census_population === undefined
        ) ?? false;
        setUniqueYearEntityPopulationData(uniqueYearData);
        setUndefinedData(hasUndefinedData)
    }, [entityPopulationData]);

    // Updated Render D3 Chart
    useEffect(() => {
      if (!uniqueYearEntityPopulationData.length || !chartRef.current) return;

      const containerWidth = containerRef.current?.clientWidth ?? 1200;
      const ratio = containerWidth > 1000 ? 0.35 : 0.6;
      const totalHeight = containerWidth * ratio;

      const svg = d3.select(chartRef.current);
      svg.selectAll("*").remove();

      const margin = { top: 20, right: 30, bottom: 60, left: 80 };
      const width = containerWidth - margin.left - margin.right;
      const height = totalHeight - margin.top - margin.bottom;

      // Set SVG attributes to match the viewBox logic
      svg.attr("viewBox", `0 0 ${containerWidth} ${totalHeight}`)
        .attr("preserveAspectRatio", "xMidYMid meet");

      const chartGroup = svg.append("g")
        .attr("transform", `translate(${margin.left},${margin.top})`);

      const processedEntityYearData = uniqueYearEntityPopulationData.map(item => ({
        ...item,
        date: new Date(item.census_year, 0, 1)
      }))
      .sort((a, b) => a.date.getTime() - b.date.getTime());

      const xDomain = d3.extent(processedEntityYearData, d => d.date);
      const x = d3.scaleUtc(
        xDomain[0] && xDomain[1] ? (xDomain as [Date, Date]) : [new Date(), new Date()], 
        [0, width] 
      );

      const yMax = d3.max(processedEntityYearData, item => item.census_population) || 0;
      const yMin = d3.min(processedEntityYearData, item => item.census_population) || 0;

      const y = d3.scaleLinear()
        .domain([yMin, yMax])
        .nice()
        .range([height, 0]);

      const line = d3.line<any>()
        .x(d => x(d.date))
        .y(d => y(d.census_population));

      // X-axis
      chartGroup.append("g")
        .attr("transform", `translate(0,${height})`)
        .call(d3.axisBottom(x).ticks(width / 80).tickSizeOuter(0))
        .selectAll("text")
        .attr("transform", "rotate(-45)")
        .style("text-anchor", "end");

      // Y-axis
      chartGroup.append("g")
        .call(d3.axisLeft(y).ticks(height / 40).tickFormat(d3.format(",d")))
        .call(g => g.select(".domain").remove())
        .call(g => g.selectAll(".tick line").clone()
            .attr("x2", width)
            .attr("stroke-opacity", 0.1))
        .append("text")
        .attr("x", -27)
        .attr("y", -10)
        .attr("fill", "currentColor")
        .attr("text-anchor", "end")
        .style("font-weight", "bold")
        .text("Population");

      chartGroup.append("path")
        .datum(processedEntityYearData)
        .attr("fill", "none")
        .attr("stroke", "#ffbc3a")
        .attr("stroke-width", 3)
        .attr("d", line);

      // Tooltip
      const tooltip = d3.select("body")
        .append("div")
        .attr("class", "tooltip")
        .style("opacity", 0)
        .style("position", "absolute")
        .style("pointer-events", "none")
        .style("background-color", "#fff")
        .style("border", "1px solid #ccc")
        .style("padding", "12px")
        .style("font-size","18px")
        .style("border-radius", "6px")
        .style("z-index", "1000");

      chartGroup.selectAll("circle")
        .data(processedEntityYearData)
        .join("circle")
        .attr("cx", d => x(d.date))
        .attr("cy", d => y(d.census_population))
        .attr("r", 5)
        .attr("fill", "#2e8b8b")
        .on("mouseover", (event, d) => {
          tooltip.transition().duration(100).style("opacity", 0.9);
          tooltip.html(`
            <b>Year: ${d.census_year}</b><br>
            Population: ${d.census_population.toLocaleString()}
          `);
          d3.select(event.currentTarget).attr("r", 8);
        })
        .on("mousemove", (event) => {
          tooltip.style("left", (event.pageX + 10) + "px")
                .style("top", (event.pageY - 28) + "px");
        })
        .on("mouseleave", (event) => {
          tooltip.transition().duration(100).style("opacity", 0);
          d3.select(event.currentTarget).attr("r", 5);
        });

      return () => {
        d3.select(".tooltip").remove();
      };
    }, [uniqueYearEntityPopulationData]);

    // Render Tabulator tale
    useEffect(() => {
       if (!tableRef.current) return;

      if (tabulatorRef.current) {
        try {
          tabulatorRef.current.setData(Array.isArray(entityPopulationData) ? entityPopulationData : [])
            .then(() => {
                if (tabulatorRef.current) {
                    tabulatorRef.current.setLayout(window.innerWidth < 1025 ? "fitDataFill" : "fitColumns");
                    tabulatorRef.current.redraw(true);
                }
            });
        } catch (err) {
        }
        return;
      }

      const mobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) && window.innerWidth < 1025;
      
      tabulatorRef.current = new Tabulator(tableRef.current, {
        minHeight: "450px",
        maxHeight: "100%",
        layout: mobile ? "fitDataFill" : "fitColumns",
        pagination: "local",
        paginationSize: 10,
        placeholder: "No population data",
        initialSort: [
            {
            column: "census_year",
            dir: "desc",
            }
        ],
        columns: [ 
          {
          titleFormatter: () => {
            const el = document.createElement("span");
            el.innerHTML = "<i class='fas fa-building'></i> Entity Name";
            return el;
          },
          title: "Entity Name",
          field: "entity_name",
          headerSort: true,
          },
          {
          titleFormatter: () => {
            const el = document.createElement("span");
            el.innerHTML = "<i class='fas fa-calendar'></i> Fiscal Year";
            return el;
          },
          title: "Fiscal Year",
          field: "census_year",
          headerSort: true,
          },
          {
          titleFormatter: () => {
            const el = document.createElement("span");
            el.innerHTML = "<i class='fas fa-people-group'></i> Population";
            return el;
          },
          title: "Population",
          field: "census_population",
          headerSort: true,
          formatter: "money",
          formatterParams: {
              thousand: ",",
              precision: 0,
          },
          },
          {
          titleFormatter: () => {
            const el = document.createElement("span");
            el.innerHTML = "<i class='fas fa-file-lines'></i> Summary Level";
            return el;
          },
          title: "Summary Level",
          field: "census_summary_level",
          headerSort: true,
          },
          {
          titleFormatter: () => {
            const el = document.createElement("span");
            el.innerHTML = "<i class='fas fa-map-location-dot'></i> Area Name";
            return el;
          },
          title: "Area Name",
          field: "census_area_name",
          headerSort: true,
          },
        ],
        data: Array.isArray(uniqueYearEntityPopulationData) ? uniqueYearEntityPopulationData : [],
        });

        setTimeout(() => {
          if (tabulatorRef.current) tabulatorRef.current.redraw(true);
        }, 100);

        const handleResize = () => {
          if (!tabulatorRef.current) return;
          const mobileResize = window.innerWidth < 1025;
          tabulatorRef.current.setLayout(mobileResize ? "fitDataFill" : "fitColumns");
          tabulatorRef.current.redraw(true);
        };

        window.addEventListener("resize", handleResize);
        return () => {
          window.removeEventListener("resize", handleResize);
          if (tabulatorRef.current) {
              tabulatorRef.current.destroy();
              tabulatorRef.current = null;
          }
        };
    }, [uniqueYearEntityPopulationData, entityPopulationData, containerRef.current?.clientWidth]); // Added containerRef.current?.clientWidth to dependancy array

  return (
    <Row className="mb-2">
      <Col xs={12}>
        <Card bg="secondary" text="white" style={{ border: "none", borderRadius: "0" }}>
          <Card.Body style={{ justifyItems: "center" }}>
            <div className="tu_tabHeader">
              <div className="tu-tabHeader__titleRow">
                <h1 className="tu-tabHeader__title">
                  Population</h1>
                  <button
                    type="button"
                    className="tu-tabHeader__infoBtn"
                    onClick={() => setShowHeaderInfo(!showHeaderInfo)}
                    aria-label="Population info"
                  >
                    <i className="fas fa-info" />
                  </button>
              </div>
            </div>

            <Collapse in={showHeaderInfo}>
              <div>
                <p className="lead mt-2 text-center">
                  The population dashboard allows users to track the population of an entity over the years.
                </p>
              </div>
            </Collapse>
          </Card.Body>
        </Card>

        {!undefinedData && uniqueYearEntityPopulationData.length > 0 ? (
          <>
            <Card className="mb-4">
              <div id="chart-container" ref={containerRef} style={{ width: "100%", padding: "20px" }}>
                <div style={{ display: "flex", gap: "10px", marginBottom: "10px" }}>
                  <div
                    className="btn btn-secondary"
                    id="btn-chart-download"
                    onClick={() => {
                      if (!chartRef.current) return;
                      const currentEntityName = parentEntityName || "Entity";
                      const bounds = chartRef.current.getBoundingClientRect();
                      const width = bounds.width;
                      const height = bounds.height;
                      const scale = 2;
                      const clonedSvg = chartRef.current.cloneNode(true) as unknown as SVGSVGElement;
                      
                      clonedSvg.setAttribute("width", width.toString());
                      clonedSvg.setAttribute("height", height.toString());

                      const serializer = new XMLSerializer();
                      const svgString = serializer.serializeToString(clonedSvg);
                      const canvas = document.createElement('canvas');
                      const context = canvas.getContext('2d');
                      const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
                      const url = URL.createObjectURL(svgBlob);

                      const img = new Image();
                      img.onload = () => {
                        canvas.width = width * scale;
                        canvas.height = height * scale;

                        if (context) {
                          context.fillStyle = "white";
                          context.fillRect(0, 0, canvas.width, canvas.height);

                          context.drawImage(img, 0, 0, canvas.width, canvas.height);
                        }
                        
                        URL.revokeObjectURL(url);
                        const pngUrl = canvas.toDataURL('image/png');
                        const a = document.createElement('a');
                        const formattedName = currentEntityName.toLowerCase().replaceAll(" ", "_");
                        a.download = `${formattedName}_population.png`;
                        a.href = pngUrl;
                        a.click();
                      };
                      img.src = url;
                    }}
                    style={{ margin: 0 }}
                  >
                    Download
                  </div>
                </div>

                <svg
                  ref={chartRef}
                  width="100%"
                  height="auto"
                  viewBox={`0 0 ${containerRef.current?.clientWidth ?? 1200} ${
                    (containerRef.current?.clientWidth ?? 1200) * 
                    ((containerRef.current?.clientWidth ?? 1200) > 1000 ? 0.35 : 0.6)
                  }`}
                  preserveAspectRatio="xMidYMid meet"
                  style={{ display: "block", 
                          maxHeight: "600px",
                          margin: "0 auto"
                  }}
                />
                <div style={{ marginTop: "12px", fontWeight: "bold", fontSize: "16px", textAlign: "center" }}>
                  {parentEntityName}
                </div>
              </div>

              <Row className="py-4 px-4">
                <Col>
                  <div className="mb-2 d-flex gap-2">
                    <button className="btn btn-secondary" onClick={() => handleDownload("csv")}>CSV</button>
                    <button className="btn btn-secondary" onClick={() => handleDownload("xlsx")}>Excel</button>
                    <button className="btn btn-secondary" onClick={() => handleDownload("html")}>HTML</button>
                    <button className="btn btn-secondary" onClick={() => handleDownload("json")}>JSON</button>
                  </div>
                  <div style={{ width: "100%" }}>
                    <div ref={tableRef} className="table-light" style={{ minHeight: 400 }} />
                  </div>
                </Col>
              </Row>
            </Card>
          </>
        ) : (
          <Card className="mb-4">
            <div className="p-5 text-center">
              <div className="alert alert-light shadow-sm" role="status">
                No data available
              </div>
            </div>
          </Card>
        )}
      </Col>
    </Row>
  );
}

export default EntityPopulationOverview;